import "./env"; // FOUND-09 — Zod validation fires at module load; throws on invalid/missing env.

// Activates the `declare module "@tanstack/react-router"` augmentation so
// route files can declare `staticData: { breadcrumb: { labelKey } }` with full
// type safety. Imported as a side-effect per CONTEXT D-09 + RESEARCH §Pitfall 3.
import "@linkhr/design-system/patterns/Breadcrumbs/router-augmentation";

import { createDefaultQueryClient } from "@linkhr/api-client";
import { createAppRoot } from "@linkhr/auth/react";
import { AuthSplash } from "@linkhr/auth/pages";
import { initI18n } from "@linkhr/i18n";
import { useBillingWarningStore, NotFoundScreen } from "@linkhr/design-system";
import { routeTree } from "./routeTree.gen";
import { env } from "./env";
import "./styles/app.css";

/**
 * PITFALL C2 — MSW must be loaded via dynamic import inside an env guard so
 * bundlers tree-shake it from production. See apps/hr-admin/src/main.tsx for
 * the canonical explanation; this file mirrors the pattern for link-admin.
 */
async function enableMocksIfConfigured(): Promise<void> {
  // Prod-tree-shake gate — literal chain required per project conventions §Vite quirks.
  if (!import.meta.env.DEV) return;

  // Runtime toggle: ?mock=1|0 > sessionStorage > localStorage > VITE_USE_MSW env.
  // Shared helper lives in @linkhr/api-client/mocks so all 3 apps share precedence.
  const { shouldEnableMsw } = await import("@linkhr/api-client/mocks");
  const { enabled, source } = shouldEnableMsw(import.meta.env.VITE_USE_MSW);

  if (!enabled) {
    console.info(`[linkhr] MSW disabled → real backend (source: ${source})`);
    return;
  }

  console.info(`[linkhr] MSW enabled (source: ${source})`);
  const { worker, WORKER_URL } = await import("./mocks/browser");
  await worker.start({
    onUnhandledRequest: "bypass",
    serviceWorker: { url: WORKER_URL },
    // MSW would log every request with its body, a typed password included.
    quiet: true,
  });
}

async function boot(): Promise<void> {
  // i18n must initialize before any useTranslation() consumer renders — the
  // auth namespace lazy-loads via i18next-http-backend from /locales/{lng}/auth.json
  // which this app serves out of public/locales/.
  await initI18n();

  // createDefaultQueryClient wires the shared meta.invalidates handler so
  // mutations declaring `meta: { invalidates: [...] }` actually refetch the
  // listed query prefixes on success.
  const queryClient = createDefaultQueryClient();

  // `aud: "platform"` — the single delta from tenant apps. Platform JWTs carry
  // `aud=platform`, `tenant_id=null`; login hits `/api/v1/_platform/auth/login`
  // (no tenantSlug in body). See @linkhr/auth/actions/platformLogin for wire shape.
  //
  // `setBillingWarning` is wired for consistency with HR Admin / Employee Web —
  // the billing middleware inside @linkhr/api-client skips `/_platform/*`
  // URLs, so Link Admin never invokes this setter in practice. BillingBanner
  // also gates on `userRoles.includes("HR_ADMIN")` (Link Admin has LINK_STAFF
  // / PLATFORM_* roles), so no banner renders even if the setter fires.
  await createAppRoot({
    aud: "platform",
    routeTree,
    apiBaseUrl: env.VITE_API_BASE_URL,
    SplashComponent: AuthSplash,
    queryClient,
    enableMocks: enableMocksIfConfigured,
    defaultRoute: "/",
    setBillingWarning: (severity) => useBillingWarningStore.getState().setBillingWarning(severity),
    DefaultNotFoundComponent: () => <NotFoundScreen homeHref={import.meta.env.BASE_URL} />,
  });
}

void boot();
